import { Alert, type AlertProps, Button, Group, Stack, Switch, Text } from '@mantine/core'
import { useLocalStorage, useOs } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconBell } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSession } from '../auth/session'
import {
  currentSubscription,
  pushSupported,
  runsInstalled,
  subscribePush,
  syncPushSubscription,
  testPush,
  unsubscribePush,
  workerReady,
} from './subscription'

/** Where this browser stands with notifications for this app */
export type PushState = 'on' | 'off' | 'denied' | 'install' | 'unsupported'

async function readPushState(apple: boolean): Promise<PushState> {
  // iPhone and iPad offer web push only to apps added to the home screen
  if (apple && !runsInstalled()) return 'install'
  if (!pushSupported() || !(await workerReady())) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  return (await currentSubscription()) ? 'on' : 'off'
}

const stateKey = ['pwa', 'push'] as const

/** Notifications on this device: the state, turning them on or off, and a test */
export function usePush() {
  const { t } = useTranslation()
  const { me } = useSession()
  const client = useQueryClient()
  const apple = useOs({ getValueInEffect: false }) === 'ios'
  const { data: state } = useQuery({ queryKey: [...stateKey, apple], queryFn: () => readPushState(apple) })
  const toggle = useMutation({
    mutationFn: (on: boolean) => (on ? subscribePush(me.user.id) : unsubscribePush()),
    onError: () => notifications.show({ color: 'danger', message: t('pwa.pushFailed') }),
    onSettled: () => client.invalidateQueries({ queryKey: stateKey }),
  })
  const test = useMutation({
    mutationFn: testPush,
    onSuccess: () => notifications.show({ message: t('pwa.pushTestSent') }),
    onError: () => notifications.show({ color: 'danger', message: t('errors.generic') }),
  })
  // A super admin looking as someone leaves this browser's notifications as they are: theirs (`usePushSync`)
  return { state, toggle, test, viewing: !!me.viewedBy }
}

/**
 * Keeps this browser's subscription linked to whoever is signed in (see `syncPushSubscription`): not while a super admin
 * looks as someone, whose notifications stay the super admin's
 */
export function usePushSync() {
  const { me } = useSession()
  const viewing = !!me.viewedBy
  useEffect(() => {
    if (!viewing) syncPushSubscription(me.user.id).catch(() => undefined)
  }, [me.user.id, viewing])
}

const hints: Record<PushState, string> = {
  on: 'pwa.pushOn',
  off: 'pwa.pushOff',
  denied: 'pwa.pushDenied',
  install: 'pwa.pushInstall',
  unsupported: 'pwa.pushUnsupported',
}

/** The notifications switch in the preferences, with what to do when it cannot be turned on */
export function PushSetting() {
  const { t } = useTranslation()
  const { state, toggle, test, viewing } = usePush()
  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Text size="sm">{t('pwa.push')}</Text>
        <Switch
          size="md"
          checked={state === 'on'}
          disabled={(state !== 'on' && state !== 'off') || toggle.isPending || viewing}
          onChange={(event) => toggle.mutate(event.currentTarget.checked)}
          aria-label={t('pwa.push')}
        />
      </Group>
      {state && (
        <Group justify="space-between" wrap="nowrap" gap="md" align="flex-start">
          <Text size="xs" c="dimmed">
            {t(hints[state])}
          </Text>
          {state === 'on' && (
            <Button variant="subtle" size="compact-sm" flex="none" loading={test.isPending} onClick={() => test.mutate()}>
              {t('pwa.pushTest')}
            </Button>
          )}
        </Group>
      )}
    </Stack>
  )
}

/** A card asking to turn notifications on, shown until they are on or the person says not now */
export function PushPrompt(props: Omit<AlertProps, 'children' | 'title'>) {
  const { t } = useTranslation()
  const { state, toggle } = usePush()
  const [dismissed, setDismissed] = useLocalStorage({ key: 'tnn.push.prompt', defaultValue: false, getInitialValueInEffect: false })
  if (dismissed || (state !== 'off' && state !== 'install')) return null
  return (
    <Alert variant="light" color="gray" radius="lg" icon={<IconBell size={20} stroke={1.75} />} title={t('pwa.promptTitle')} {...props}>
      <Text size="sm">{t(state === 'install' ? 'pwa.pushInstall' : 'pwa.promptBody')}</Text>
      <Group gap="xs" mt="sm">
        {state === 'off' && (
          <Button size="sm" loading={toggle.isPending} onClick={() => toggle.mutate(true)}>
            {t('pwa.promptAction')}
          </Button>
        )}
        <Button size="sm" variant="subtle" onClick={() => setDismissed(true)}>
          {t('pwa.later')}
        </Button>
      </Group>
    </Alert>
  )
}
